<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { API, HOST } from '@/utils/http'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from '@/components/ui/toast'
import { Plus } from 'lucide-vue-next'
import centroid from '@turf/centroid'
import distance from '@turf/distance'
import booleanWithin from '@turf/boolean-within'

const COLLECTED_DATA_LAYER_TYPE = 4

const props = defineProps({
  orgId: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  feature: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['formSubmitted'])

const store = useStore()

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const aoiGeometry = computed(() => {
  const g = props.feature
  if (!g) return null
  return g.type === 'Feature' ? g.geometry : g
})

const modelMode = ref('new')
const modelsInitialized = ref(false)

const now = new Date()
const yearOptions = Array.from({ length: 7 }, (_, i) => String(now.getFullYear() - i))
const monthOptions = Array.from({ length: 12 }, (_, i) => String(i + 1))
const selectedYear = ref(String(now.getFullYear()))
const selectedMonth = ref(String(now.getMonth() + 1))

const trainingMonthFilter = computed(() =>
  `${selectedYear.value}_${selectedMonth.value.padStart(2, '0')}`
)

const existingModels = ref([])
const existingModelsLoading = ref(false)
const selectedModelCode = ref(null)

const selectedExistingModel = computed(
  () => existingModels.value.find(m => m.code === selectedModelCode.value) || null
)

// Client-side classifier: given an existing model's training_aoi and the user's
// classify AOI, label the pair as same / nearby / distant.
function relationshipTag(trainingAoi, classifyAoi) {
  if (!trainingAoi || !classifyAoi) return null
  try {
    const trainFeature = { type: 'Feature', geometry: trainingAoi, properties: {} }
    const classifyFeature = { type: 'Feature', geometry: classifyAoi, properties: {} }
    try {
      if (booleanWithin(classifyFeature, trainFeature)) return { label: 'same AOI', tone: 'good' }
    } catch (_) {
      // booleanWithin is strict about geometry types; fall through to distance.
    }
    const trainCentroid = centroid(trainFeature)
    const classifyCentroid = centroid(classifyFeature)
    const km = distance(trainCentroid, classifyCentroid, { units: 'kilometers' })
    if (km <= 100) return { label: 'nearby', tone: 'ok' }
    return { label: 'distant', tone: 'warn', km: Math.round(km) }
  } catch (e) {
    return null
  }
}

const fetchExistingModels = async () => {
  existingModelsLoading.value = true
  try {
    const res = await API.get(`${HOST}/org/${props.orgId}/lulc/models`)
    existingModels.value = res.data || []
  } catch (error) {
    console.error('Failed to fetch LULC models:', error)
    existingModels.value = []
  } finally {
    existingModelsLoading.value = false
    if (!modelsInitialized.value) {
      modelsInitialized.value = true
      if (existingModels.value.length > 0) {
        modelMode.value = 'existing'
        const firstReady = existingModels.value.find(m => m.status === 0) || existingModels.value[0]
        selectedModelCode.value = firstReady.code
      } else {
        modelMode.value = 'new'
      }
    }
  }
}

const collectedLayers = ref([])
const layersLoading = ref(false)
const selectedLayerCode = ref(null)
const templates = ref([])
const templatesLoading = ref(false)
const selectedTemplateIds = ref([])

const selectedLayer = computed(
  () => collectedLayers.value.find(l => l.code === selectedLayerCode.value) || null
)

const classificationStartYear = ref(String(now.getFullYear()))
const classificationStartMonth = ref(String(now.getMonth() + 1))
const classificationEndYear = ref(String(now.getFullYear()))
const classificationEndMonth = ref(String(now.getMonth() + 1))

const formatMonthFilter = (year, month) => `${year}_${String(month).padStart(2, '0')}`

const classificationStartFilter = computed(
  () => formatMonthFilter(classificationStartYear.value, classificationStartMonth.value)
)
const classificationEndFilter = computed(
  () => formatMonthFilter(classificationEndYear.value, classificationEndMonth.value)
)

const classificationRangeValid = computed(
  () => classificationEndFilter.value >= classificationStartFilter.value
)

const isSubmitting = ref(false)

const canSubmit = computed(() => {
  if (!props.feature || !classificationRangeValid.value || isSubmitting.value) {
    return false
  }
  if (modelMode.value === 'new') {
    return (
      !!selectedYear.value &&
      !!selectedMonth.value &&
      !!selectedLayer.value &&
      selectedTemplateIds.value.length > 0
    )
  }
  return !!selectedExistingModel.value
})

const fetchCollectedLayers = async () => {
  layersLoading.value = true
  try {
    const res = await API.get(`${HOST}/org/${props.orgId}/layers/all`)
    collectedLayers.value = (res.data || []).filter(l => l.type === COLLECTED_DATA_LAYER_TYPE)
  } catch (error) {
    console.error('Failed to fetch layers:', error)
    collectedLayers.value = []
  } finally {
    layersLoading.value = false
  }
}

const fetchTemplates = async () => {
  templates.value = []
  selectedTemplateIds.value = []
  if (!props.feature || !selectedLayer.value) return

  templatesLoading.value = true
  try {
    const res = await API.post(
      `${HOST}/org/${props.orgId}/layers/${selectedLayer.value.code}/label-templates`,
      { aoi: aoiGeometry.value }
    )
    templates.value = res.data || []
    selectedTemplateIds.value = templates.value.map(t => t.template_id)
  } catch (error) {
    console.error('Failed to fetch label templates:', error)
    templates.value = []
    toast({
      title: 'Could not load labeled templates',
      description: error?.response?.data?.detail || 'Unknown error',
      variant: 'destructive',
    })
  } finally {
    templatesLoading.value = false
  }
}

watch(() => [props.feature, selectedLayerCode.value], () => {
  fetchTemplates()
})

watch(modelMode, (mode) => {
  if (mode === 'existing' && existingModels.value.length === 0) {
    fetchExistingModels()
  }
})

const toggleTemplate = (templateId, checked) => {
  const idx = selectedTemplateIds.value.indexOf(templateId)
  if (checked && idx === -1) {
    selectedTemplateIds.value.push(templateId)
  } else if (!checked && idx !== -1) {
    selectedTemplateIds.value.splice(idx, 1)
  }
}

const startClassification = async () => {
  if (!canSubmit.value) return

  isSubmitting.value = true
  try {
    const modelPayload = modelMode.value === 'new'
      ? {
          mode: 'new',
          training_layer_id: selectedLayer.value.id,
          template_ids: selectedTemplateIds.value,
          training_month: trainingMonthFilter.value,
        }
      : {
          mode: 'existing',
          model_code: selectedExistingModel.value.code,
        }

    const res = await API.post(`${HOST}/org/${props.orgId}/lulc/classify`, {
      aoi: aoiGeometry.value,
      name: props.name,
      model: modelPayload,
      start_month: classificationStartFilter.value,
      end_month: classificationEndFilter.value,
    })

    toast({
      title: 'Classification queued',
    })

    // LULC creates multiple layers and the API returns only codes, so refresh
    // the store from the server instead of synthesising layer objects. Then
    // emit null so LayerCreate's handler skips its own addOrgLayer (which would
    // double-insert) and just navigates back to the sidebar.
    try {
      await store.dispatch('layers/fetchOrgLayers', { orgId: props.orgId })
    } catch (e) {
      console.warn('Layer list refresh failed:', e)
    }

    emit('formSubmitted', null)
  } catch (error) {
    console.error('Classification pipeline error:', error)
    toast({
      title: 'Failed to start classification',
      description: error?.response?.data?.detail || 'Unknown error',
      variant: 'destructive',
    })
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  fetchCollectedLayers()
  fetchExistingModels()
})
</script>

<template>
  <div class="space-y-5">
    <section
      v-if="existingModels.length > 0 && modelMode === 'existing'"
      class="space-y-2"
    >
      <h3 class="text-sm font-semibold">Model</h3>
      <div class="rounded border">
        <Select v-model="selectedModelCode">
          <SelectTrigger class="border-0 focus:ring-0 focus:ring-offset-0">
            <SelectValue
              :placeholder="existingModelsLoading ? 'Loading…' : 'Select a trained model'"
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="m in existingModels"
              :key="m.code"
              :value="m.code"
            >
              {{ m.name || `Model ${String(m.code).slice(0, 8)}` }} — {{ m.status_label }}
            </SelectItem>
          </SelectContent>
        </Select>
        <div
          v-if="selectedExistingModel"
          class="text-xs border-t px-2 py-1 space-y-1"
        >
          <div class="flex items-center gap-2">
            <span class="font-medium">Training month:</span>
            <span>{{ selectedExistingModel.training_month || '—' }}</span>
          </div>
          <div
            v-if="(selectedExistingModel.class_names || []).length"
            class="flex items-start gap-2"
          >
            <span class="font-medium shrink-0">Labels:</span>
            <span>{{ (selectedExistingModel.class_names || []).join(', ') }}</span>
          </div>
        </div>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        class="w-full"
        @click="modelMode = 'new'"
      >
        <Plus class="w-4 h-4 mr-1" />
        New model
      </Button>
    </section>

    <section
      v-if="existingModels.length > 0 && modelMode === 'new'"
      class="flex items-center justify-between"
    >
      <h3 class="text-sm font-semibold">New model</h3>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        @click="modelMode = 'existing'"
      >
        Use existing instead
      </Button>
    </section>

    <section v-if="modelMode === 'new'" class="space-y-2">
      <h3 class="text-sm font-semibold">Training Basemap</h3>
      <p class="text-xs text-muted-foreground">
        Pick the basemap period that matches when your training labels were collected — the model
        learns from imagery aligned to the labels.
      </p>
      <div class="flex space-x-2">
        <Select v-model="selectedYear">
          <SelectTrigger>
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="y in yearOptions" :key="y" :value="y">{{ y }}</SelectItem>
          </SelectContent>
        </Select>
        <Select v-model="selectedMonth">
          <SelectTrigger>
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="m in monthOptions" :key="m" :value="m">
              {{ monthNames[Number(m) - 1] }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </section>

    <section v-if="modelMode === 'new'" class="space-y-3">
      <h3 class="text-sm font-semibold">Training Labels</h3>

      <div class="space-y-2">
        <label class="text-sm font-medium">Data layer</label>
        <Select v-model="selectedLayerCode">
          <SelectTrigger>
            <SelectValue
              :placeholder="layersLoading ? 'Loading…' : 'Select a collected data layer'"
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="layer in collectedLayers"
              :key="layer.code"
              :value="layer.code"
            >
              {{ layer.name }}
            </SelectItem>
          </SelectContent>
        </Select>
        <p
          v-if="!layersLoading && collectedLayers.length === 0"
          class="text-xs text-muted-foreground"
        >
          This organization has no collected data layers.
        </p>
      </div>

      <div v-if="selectedLayer" class="space-y-2">
        <label class="text-sm font-medium">Templates to use as labels</label>
        <p v-if="templatesLoading" class="text-xs text-muted-foreground">Loading…</p>
        <p
          v-else-if="templates.length === 0"
          class="text-xs text-muted-foreground"
        >
          No labeled polygons from this layer intersect the selected AOI.
        </p>
        <ul v-else class="max-h-[220px] overflow-y-auto border rounded divide-y">
          <li
            v-for="tpl in templates"
            :key="tpl.template_id"
            class="flex items-center gap-3 px-3 py-2"
          >
            <Checkbox
              :id="`lulc-tpl-${tpl.template_id}`"
              :model-value="selectedTemplateIds.includes(tpl.template_id)"
              @update:model-value="(v) => toggleTemplate(tpl.template_id, v)"
            />
            <span
              class="inline-block w-3 h-3 rounded-sm border"
              :style="{ backgroundColor: tpl.color || '#999' }"
            />
            <label
              :for="`lulc-tpl-${tpl.template_id}`"
              class="flex-1 text-sm cursor-pointer"
            >
              {{ tpl.name || `Template #${tpl.template_id}` }}
            </label>
            <span class="text-xs text-muted-foreground">{{ tpl.feature_count }}</span>
          </li>
        </ul>
      </div>
    </section>

    <section
      v-if="modelMode === 'existing'
        ? !!selectedExistingModel
        : (!!selectedLayer && selectedTemplateIds.length > 0)"
      class="space-y-3"
    >
      <div class="space-y-1">
        <h3 class="text-sm font-semibold mb-0">Classification Range</h3>
        <p class="text-xs text-muted-foreground">
          Pick the first and last month to classify.
        </p>
      </div>
      <div class="space-y-2">
        <div class="flex items-center space-x-2">
          <span class="text-xs text-muted-foreground w-10 shrink-0">From</span>
          <Select v-model="classificationStartYear">
            <SelectTrigger>
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="y in yearOptions" :key="y" :value="y">{{ y }}</SelectItem>
            </SelectContent>
          </Select>
          <Select v-model="classificationStartMonth">
            <SelectTrigger>
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="m in monthOptions" :key="m" :value="m">
                {{ monthNames[Number(m) - 1] }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div class="flex items-center space-x-2">
          <span class="text-xs text-muted-foreground w-10 shrink-0">To</span>
          <Select v-model="classificationEndYear">
            <SelectTrigger>
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="y in yearOptions" :key="y" :value="y">{{ y }}</SelectItem>
            </SelectContent>
          </Select>
          <Select v-model="classificationEndMonth">
            <SelectTrigger>
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="m in monthOptions" :key="m" :value="m">
                {{ monthNames[Number(m) - 1] }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <p
        v-if="!classificationRangeValid"
        class="text-xs text-destructive"
      >
        End month must be on or after the start month.
      </p>
    </section>

    <Button
      class="w-full"
      :disabled="!canSubmit"
      @click="startClassification"
    >
      <span v-if="isSubmitting">Running…</span>
      <span v-else>Create</span>
    </Button>
  </div>
</template>
